
import { StatusCodes } from "@montana/constants/status-codes";
import { staticSettings } from "@montana/helpers/static-settings.helper";
import { refreshSecurityToken } from "../authentication";
import { getToken } from "@montana/helpers/auth-token.helper";
import { IRequestHeaderConfig } from "@montana/models/dev";
import { isBrowser } from "@montana/helpers/browser.helper";
import { CUSTOMER_MODE } from "@montana/constants/customer-mode";

type IHeaders = RequestInit["headers"];

export const CANCELLED = "cancelled";

function addAuthHeader(extraHeaders: IHeaders): IHeaders {
    const token = getToken();

    if (!staticSettings.b2b || !token) {
        return extraHeaders;
    }

    return {...extraHeaders, Authorization: `Bearer ${token}`}
}

export function fetchAuth(input: any, init: RequestInit = {}): Promise<Response> {
    return fetch(input, { ...init, headers: addAuthHeader(init.headers) })
        .then((res) => {
            if (res.status === StatusCodes.Error) {
                throw (res);
            }

            if (res.status !== StatusCodes.Unauthorized) {
                return res;
            }

            return refreshSecurityToken()
                .then((refreshResponse: Response) => {
                    // If we are still 401 when trying to refresh our token, simply return the failure.
                    if (refreshResponse.status !== StatusCodes.Success) {
                        throw (res);
                    }
                })
                .then(() => fetchAuth(input, init))
                .catch(res => res);
        });
}

export type IServiceFunction<TArgs extends any[], TReturn = Promise<Response>> = (config: IRequestHeaderConfig, ...args: TArgs) => TReturn;

function getHeaders(config: IRequestHeaderConfig, extraHeaders?: IHeaders): IHeaders {
    const headers: { [key: string]: string } = {};

    if (config.language) {
        headers["language"] = config.language;
    }

    const marketId = config.market?.marketId;
    if (marketId) {
        headers["MarketId"] = marketId;
    }

    const customerType = staticSettings.b2b ? CUSTOMER_MODE.B2B : config.customerMode;
    if (customerType) {
        headers["CustomerType"] = customerType;
    }

    return { ...headers, ...extraHeaders };
}

/**
 * @author SBF
 *
 * @description
 * Automatically adds header config argument to service function.
 * Use returned function with "useConnectedServiceFunctions" hook for use in componenets or "connectServiceFunctions" generator function for use in redux saga.
 *
 * @param cb Function to have config added to args
 * @param req Optional fetching function. Defaults to fetchAuth.
 *
 * @example
 * const someFunction = createServiceFunction((arg1: string, arg2: number) => ...) // returns (config, arg1, arg2) => ...
 */
export function createServiceFunction<TArgs extends any[], TResponse = Response>(
    cb: (...args: TArgs) => (req: any) => Promise<TResponse>,
    req: any = fetchAuth
): (config: IRequestHeaderConfig, ...args: TArgs) => Promise<TResponse> {
    return (config: IRequestHeaderConfig, ...args: TArgs) => {
        const reqProxy: any = (input: any, init: any) => req(input, { ...init, headers: getHeaders(config, init?.headers) });

        return cb(...args)(reqProxy);
    };
}

/**
 * @author SBF
 *
 * @description
 * Automatically adds header config argument to service function.
 * Also adds cancellation with AbortController, use for service functions which are expected to be invoked in quick succession.
 * Use returned function with "useConnectedServiceFunctions" hook for use in componenets or "connectServiceFunctions" generator function for use in redux saga.
 *
 * @param cb Function to have config added to args
 * @param req Optional fetching function. Defaults to fetchAuth.
 *
 * @example
 * const someFunction = createCancellableServiceFunction((arg1: string, arg2: number) => ...) // returns (config, arg1, arg2) => ...
 */
export function createCancellableServiceFunction<TArgs extends any[], TResponse = Response>(
    cb: (...args: TArgs) => (req: any) => Promise<TResponse>,
    req: any = fetchAuth
): (config: IRequestHeaderConfig, ...args: TArgs) => Promise<TResponse | typeof CANCELLED> {
    let abortController = isBrowser ? new AbortController() : undefined;

    function cancelPrevious(): void {
        if (!abortController) {
            return;
        }

        abortController.abort();
        abortController = new AbortController();
    }

    return async (config: IRequestHeaderConfig, ...args: TArgs) => {
        cancelPrevious();

        const reqProxy: any = (input: any, init: any) => req(input, { ...init, headers: getHeaders(config, init?.headers), signal: abortController?.signal });

        try {
            return await cb(...args)(reqProxy);
        } catch {
            return CANCELLED;
        }

    };
}

/**
 * @author SBF
 *
 * @description
 * Used to add config to function and remove from signature in returned function.
 * This would usually only be consumed by hook or generator function to connect service functions to redux store.
 *
 * @param config Config object for headers to consume
 * @param fun Function to add config to
 */
export function addConfigToServiceFunction<TArgs extends any[], TResponse>(
    config: IRequestHeaderConfig,
    fun: (config: IRequestHeaderConfig, ...args: TArgs) => Promise<TResponse>
): (...args: TArgs) => Promise<TResponse> {
    return (...args: TArgs) => fun(config, ...args);
}
